import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  onOperatorsAuthExpired,
  onOperatorsAuthSessionRefreshed,
  type OperatorsAuthSession,
  type OperatorsAuthTool,
} from "../operatorsAuthSession";
import { isDashboardsOnly } from "../dashboardsOnly";

type AccessContextValue = {
  can: (permission: string) => boolean;
  flags: Readonly<Record<string, boolean>>;
  isSuperAdmin: boolean;
  permissions: ReadonlySet<string>;
  roles: readonly string[];
  tools: readonly OperatorsAuthTool[];
};

const AccessContext = createContext<AccessContextValue | null>(null);

function accessFromSession(session: OperatorsAuthSession | null): {
  flags: Readonly<Record<string, boolean>>;
  isSuperAdmin: boolean;
  permissions: ReadonlySet<string>;
  roles: readonly string[];
  tools: readonly OperatorsAuthTool[];
} {
  const tools = session?.tools ?? [];
  return {
    flags: session?.flags ?? {},
    isSuperAdmin: session?.isSuperAdmin === true,
    permissions: new Set(session?.permissions ?? []),
    roles: session?.roles ?? [],
    // A dashboards preview has no Operator session transport, so the tool is
    // dropped here rather than in the nav: useHasTool reads the same context,
    // and that is what guards the route.
    tools: isDashboardsOnly() ? tools.filter((tool) => tool.slug !== "operators") : tools,
  };
}

export function AccessProvider({
  children,
  initialSession = null,
}: {
  children: ReactNode;
  /** Optional seed for tests / first paint before auth refresh events fire. */
  initialSession?: OperatorsAuthSession | null;
}) {
  const seeded = accessFromSession(initialSession);
  const [permissions, setPermissions] = useState<ReadonlySet<string>>(() => seeded.permissions);
  const [roles, setRoles] = useState<readonly string[]>(() => seeded.roles);
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => seeded.isSuperAdmin);
  const [tools, setTools] = useState<readonly OperatorsAuthTool[]>(() => seeded.tools);
  const [flags, setFlags] = useState<Readonly<Record<string, boolean>>>(() => seeded.flags);

  useEffect(() => {
    const applySession = (session: OperatorsAuthSession | null) => {
      const next = accessFromSession(session);
      setPermissions(next.permissions);
      setRoles(next.roles);
      setIsSuperAdmin(next.isSuperAdmin);
      setTools(next.tools);
      setFlags(next.flags);
    };

    const unsubscribeRefresh = onOperatorsAuthSessionRefreshed((session) => {
      applySession(session);
    });
    const unsubscribeExpired = onOperatorsAuthExpired(() => {
      applySession(null);
    });

    return () => {
      unsubscribeRefresh();
      unsubscribeExpired();
    };
  }, []);

  const value = useMemo<AccessContextValue>(
    () => ({
      can: (permission: string) => isSuperAdmin || permissions.has(permission),
      flags,
      isSuperAdmin,
      permissions,
      roles,
      tools,
    }),
    [flags, isSuperAdmin, permissions, roles, tools],
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("AccessProvider is required");
  }
  return context;
}

export function usePermission(permission: string): boolean {
  return useAccess().can(permission);
}

export function usePermissions(): ReadonlySet<string> {
  return useAccess().permissions;
}

export function useIsSuperAdmin(): boolean {
  return useAccess().isSuperAdmin;
}

export function useRoles(): readonly string[] {
  return useAccess().roles;
}

export function useTools(): readonly OperatorsAuthTool[] {
  return useAccess().tools;
}

export function useHasTool(slug: string): boolean {
  return useAccess().tools.some((tool) => tool.slug === slug);
}

export function useFlags(): Readonly<Record<string, boolean>> {
  return useAccess().flags;
}

export function useFlag(key: string): boolean {
  return useAccess().flags[key] === true;
}
